import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, CheckCircle2, AlertTriangle, RefreshCw, PencilLine } from "lucide-react";
import { adminFixtures, apiError } from "@/lib/adminApi";
import { COMMON_TIMEZONES, utcIsoToLocal, fmtEvent, fmtEventDate } from "@/lib/time";

export const ADMIN_TZ = "Australia/Perth";

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <span className="mt-1 block font-mono text-[10px] text-slate-600">{hint}</span>}
  </label>
);

function FixtureCombobox({ fixtures, value, onPick, isLoading }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = fixtures.find((f) => f.id === value);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return fixtures.filter((f) => f.status_state === "scheduled" || f.id === value).filter((f) => !needle || f.matchup.toLowerCase().includes(needle) || `${f.home_abbr} ${f.away_abbr}`.toLowerCase().includes(needle));
  }, [fixtures, q, value]);
  const byDay = useMemo(() => list.reduce((acc, f) => { const d = fmtEventDate(f.start_utc, ADMIN_TZ); (acc[d] ||= []).push(f); return acc; }, {}), [list]);
  return (
    <div className="relative" data-testid="fixture-combobox">
      <Search size={14} className="pointer-events-none absolute left-3 top-3 text-slate-500" />
      <input data-testid="pick-fixture-search" className="admin-input !pl-9" placeholder={isLoading ? "Loading verified NBA fixtures…" : selected ? `${selected.matchup} · ${fmtEvent(selected.start_utc, ADMIN_TZ)} AWST` : "Search team or matchup (next 14 days)…"}
        value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && !isLoading && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-[#2A364F] bg-[#0F131D] shadow-2xl" data-testid="fixture-options">
          {list.length === 0 && <p className="px-3 py-3 font-mono text-[11px] text-slate-500">No scheduled games match "{q}".</p>}
          {Object.entries(byDay).map(([day, games]) => (
            <div key={day}>
              <p className="sticky top-0 bg-[#141A26] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E676]">{day} · AWST</p>
              {games.map((g) => (
                <button type="button" key={g.id} data-testid={`fixture-option-${g.id}`} onMouseDown={() => { onPick(g.id); setQ(""); setOpen(false); }} className={`flex w-full items-center justify-between px-3 py-2 text-left hover:bg-[#00E676]/10 ${g.id === value ? "bg-[#00E676]/10" : ""}`}>
                  <span className="text-sm text-slate-200"><b className="text-white">{g.away_abbr}</b> {g.away_name} <span className="text-slate-500">@</span> <b className="text-white">{g.home_abbr}</b> {g.home_name}</span>
                  <span className="ml-3 shrink-0 font-mono text-[11px] text-slate-400">{fmtEvent(g.start_utc, ADMIN_TZ, { weekday: undefined, day: undefined, month: undefined })}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FixturePicker({ form, set, setForm, onFixtures }) {
  const isNba = form.sport === "nba";
  const [manual, setManual] = useState(false);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({ queryKey: ["admin-fixtures", form.sport], queryFn: () => adminFixtures({ sport: form.sport, days: 14 }), enabled: isNba, retry: 1 });
  const fixtures = useMemo(() => data?.fixtures || [], [data]);
  useEffect(() => { onFixtures?.(fixtures); }, [fixtures]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (id) => {
    const g = fixtures.find((f) => f.id === id);
    if (!g) return;
    const opp = form.player_team_id ? (g.home_id === form.player_team_id ? g.away_id : g.away_id === form.player_team_id ? g.home_id : "") : "";
    setForm((f) => ({ ...f, event_id: id, game: g.matchup, event_time_local: utcIsoToLocal(g.start_utc, f.event_tz), manual_confirmed: false, opponent_team_id: opp }));
    setManual(false);
  };
  const selected = fixtures.find((f) => f.id === form.event_id);
  const verified = !!selected;
  const showManual = !isNba || manual || (!!form.event_id && !isLoading && !isError && !verified);

  return (
    <>
      {isNba && (
        <Field label="Verified NBA fixture (BALLDONTLIE · next 14 days)" hint={verified ? undefined : "Pick the game first — matchup, tip-off time and game ID are filled automatically."}>
          {isError ? (
            <div className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-3" data-testid="fixture-error">
              <p className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-wider text-[#EF4444]"><AlertTriangle size={13} /> Fixture data could not be retrieved</p>
              <p className="mt-1 font-mono text-[11px] text-slate-300">{apiError(error)}</p>
              <div className="mt-2 flex gap-2">
                <button type="button" data-testid="fixture-retry" onClick={() => refetch()} className="btn-outline !px-3 !py-1.5 text-xs"><RefreshCw size={12} className={isFetching ? "animate-spin" : ""} /> Retry</button>
                <button type="button" data-testid="fixture-manual-fallback" onClick={() => setManual(true)} className="rounded-md border border-[#F59E0B]/40 px-3 py-1.5 font-mono text-[11px] uppercase text-[#F59E0B]"><PencilLine size={12} className="inline" /> Enter fixture manually</button>
              </div>
            </div>
          ) : (
            <FixtureCombobox fixtures={fixtures} value={form.event_id} onPick={pick} isLoading={isLoading} />
          )}
        </Field>
      )}

      {verified && (
        <div className="rounded-lg border border-[#00E676]/30 bg-[#00E676]/5 p-3" data-testid="fixture-verified-note">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#00E676]"><CheckCircle2 size={13} /> Verified against the official NBA schedule · game #{selected.id}</p>
          <p className="mt-1 font-display text-base font-bold uppercase text-white" data-testid="fixture-selected-matchup">{selected.matchup}</p>
          <p className="font-mono text-[11px] text-slate-400" data-testid="fixture-selected-time">{fmtEvent(selected.start_utc, ADMIN_TZ)} AWST · {fmtEvent(selected.start_utc, "America/New_York")} ET · {fmtEvent(selected.start_utc, "Europe/London")} UK</p>
          <p className="mt-1 font-mono text-[10px] text-slate-600">Tip-off re-synced hourly from BALLDONTLIE; schedule changes update the published pick automatically.</p>
        </div>
      )}

      {isNba && !verified && !isError && !manual && (
        <button type="button" data-testid="fixture-manual-toggle" onClick={() => setManual(true)} className="font-mono text-[10px] uppercase tracking-wider text-slate-500 underline-offset-2 hover:text-[#F59E0B] hover:underline">Game not listed? Enter fixture manually (fallback)</button>
      )}

      {showManual && !verified && (
        <div className="space-y-4 rounded-lg border border-[#F59E0B]/30 p-4" data-testid="manual-fixture-section">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B]">Manual fixture {isNba ? "(fallback — no verified schedule)" : "(no schedule provider for this sport)"}</p>
          <Field label="Matchup" hint="Away Team @ Home Team"><input data-testid="pick-game-input" className="admin-input" placeholder={isNba ? "Boston Celtics @ New York Knicks" : "Arsenal vs Chelsea"} value={form.game} onChange={set("game")} required /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Event start (local to the time zone →)"><input data-testid="pick-game-time-input" type="datetime-local" className="admin-input" value={form.event_time_local} onChange={set("event_time_local")} /></Field>
            <Field label="Event time zone"><select data-testid="pick-event-tz-select" className="admin-input" value={form.event_tz} onChange={(e) => setForm((f) => ({ ...f, event_tz: e.target.value }))}>
              {COMMON_TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
            </select></Field>
          </div>
          <label className="flex items-start gap-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3 text-xs text-[#F59E0B]" data-testid="fixture-manual-warning">
            <input type="checkbox" data-testid="pick-manual-confirm-checkbox" checked={form.manual_confirmed} onChange={set("manual_confirmed")} className="mt-0.5" />
            <span><AlertTriangle size={12} className="mr-1 inline" /> Unverified fixture. I confirm this event, these teams and this start time are real. (Required before publishing.)</span>
          </label>
          {isNba && <button type="button" data-testid="fixture-back-to-verified" onClick={() => { setManual(false); setForm((f) => ({ ...f, event_id: "" })); }} className="font-mono text-[10px] uppercase tracking-wider text-slate-500 hover:text-[#00E676]">← Back to verified fixtures</button>}
        </div>
      )}
    </>
  );
}
